import { motion } from "framer-motion";
import { t } from "i18next";
import type React from "react";
import type { ReactNode } from "react";
import { memo, useEffect, useRef, useState } from "react";
import { Icon } from "../../../../inJoy-components/Icon/Icon";
import { FlameAnimation } from "../Animation/FlameAnimation";

interface TeamStats {
	label: string; // e.g., "FG"
	value: string; // e.g., "45 / 81"
	progress: number; // 0 to 100
}

interface TeamData {
	name: string;
	score: number;
	icon?: ReactNode;
	isWinner?: boolean;
	stats?: TeamStats;
	energy?: number; // 0 to 100
}

interface KioskScoreBoardProps {
	homeTeam: TeamData;
	awayTeam: TeamData;
	time: string;
	isProcessing?: boolean;
	gameState?: string;
	className?: string;
}

const SegmentedProgressBar: React.FC<{
	progress: number;
	gameState: string;
	className?: string;
	hasAccentBar?: boolean;
}> = ({ progress, gameState, className = "", hasAccentBar }) => {
	return (
		<div
			className={`relative w-full h-[14px] ${className}`}
			style={{
				maskImage: "url(/img/event-page/progress_bar_mask.svg)",
				WebkitMaskImage: "url(/img/event-page/progress_bar_mask.svg)",
				maskSize: "100% 100%",
				WebkitMaskSize: "100% 100%",
				maskRepeat: "no-repeat",
				WebkitMaskRepeat: "no-repeat",
			}}
		>
			<div className="absolute inset-0 bg-[#696969]" />
			<motion.div
				className="absolute inset-0 bg-brand-linear-h"
				initial={{ clipPath: "inset(0 100% 0 0)" }}
				animate={{ clipPath: `inset(0 ${100 - progress}% 0 0)` }}
				transition={{ duration: 0.5, ease: "easeOut" }}
			/>
			{gameState === "trigger_anim" && hasAccentBar && (
				<motion.div
					className="absolute inset-0"
					style={{
						background:
							"linear-gradient(90deg, #F79B29 0%, #C82100 25%, #F79B29 50%, #C82100 75%, #F79B29 100%)",
						backgroundSize: "200% 100%",
					}}
					animate={{ backgroundPosition: ["100% 0%", "0% 0%"] }}
					transition={{
						duration: 1.5,
						ease: "linear",
						repeat: Infinity,
					}}
				/>
			)}
		</div>
	);
};

const TeamScoreContainer: React.FC<{
	team: TeamData;
	gameState: string;
	align: "left" | "right";
	hasAccentBar?: boolean;
}> = ({ team, gameState, align, hasAccentBar }) => {
	const isLeft = align === "left";

	// Score change animation
	const prevScore = useRef(team.score);
	const [scorePulse, setScorePulse] = useState(false);

	useEffect(() => {
		if (team.score !== prevScore.current) {
			prevScore.current = team.score;
			setScorePulse(true);
			const timer = setTimeout(() => setScorePulse(false), 2000);
			return () => clearTimeout(timer);
		}
	}, [team.score]);

	return (
		<div
			className={`relative flex flex-1 h-[140px] items-stretch bg-kiosk-surface-secondary ${!isLeft ? "flex-row-reverse" : ""}`}
		>
			{/* bgs */}
			<div
				className={`absolute ${isLeft ? "left-0" : "right-0"} w-[100px] h-full ${gameState === "trigger_anim" && hasAccentBar ? "bg-kiosk-surface-secondary" : "bg-kiosk-surface-primary"} `}
			></div>
			{gameState === "trigger_anim" && hasAccentBar && (
				<motion.div
					className="absolute inset-0 bg-gradient-fire-v origin-bottom"
					initial={{ scaleY: 0 }}
					animate={{ scaleY: 1 }}
					transition={{ duration: 0.5, ease: "easeOut" }}
				/>
			)}

			{/* Accent Bar */}
			{hasAccentBar && (
				<div
					className={`relative w-[6px] shrink-0 h-full ${gameState === "trigger_anim" ? "bg-kiosk-fire" : "bg-kiosk-accent"}`}
				/>
			)}

			{/* Icon Container */}
			<div className="relative w-[100px] h-full flex flex-col items-center justify-center shrink-0">
				{gameState === "trigger_anim" && hasAccentBar && (
					<FlameAnimation
						className="absolute bottom-9"
						width={68}
						height={150}
					/>
				)}
				<div className="relative">{team.icon}</div>
			</div>

			{/* Score Details Container */}
			<div className="relative flex-1 w-[234px] px-[20px] flex flex-col justify-center gap-1">
				{/* Top Row: Info + Score + Badge */}
				<div
					className={`flex items-center justify-between w-full ${!isLeft ? "flex-row-reverse" : ""}`}
				>
					{/* Score Info */}
					<div
						className={`w-full flex flex-col justify-between ${!isLeft ? "items-end text-right" : "items-start text-left"}`}
					>
						<span className="text-body-default-b text-white/70">
							{team.name.toUpperCase()}
						</span>

						<div className="w-full flex items-center justify-between">
							<span
								className={`font-display font-bold text-[50px] leading-none transition-all duration-150 ease-[cubic-bezier(0.4,0,0.2,1)] ${gameState === "trigger_anim" && hasAccentBar ? " text-gradient-fire text-white" : "text-kiosk-accent"} ${scorePulse ? "scale-110" : "scale-100"}`}
							>
								{team.score}
							</span>
							{gameState === "trigger_anim" && hasAccentBar && (
								<div className="flex items-center gap-1 text-kiosk-fire">
									<Icon iconName="fire" size="Medium" />
									<span className="text-body-default-b">ON FIRE</span>
								</div>
							)}
						</div>

						{/* Stats Row */}
						{team.stats && (
							<div
								className={`flex items-center gap-[8px] text-body-sm text-white ${!isLeft ? "flex-row-reverse" : ""}`}
							>
								<span>{team.stats.value}</span>
								<span>{team.stats.label}</span>
							</div>
						)}
					</div>
				</div>

				{/* Bottom Row: Progress Bar */}
				{/* If energy is present, otherwise use stats progress if needed, or default to empty/visual */}
				<SegmentedProgressBar
					progress={
						team.energy !== undefined ? team.energy : team.stats?.progress || 0
					}
					gameState={gameState}
					hasAccentBar={hasAccentBar}
					className={!isLeft ? "-scale-x-100" : ""}
				/>
			</div>
		</div>
	);
};

export const KioskScoreBoard: React.FC<KioskScoreBoardProps> = memo(
	({
		homeTeam,
		awayTeam,
		time,
		isProcessing = false,
		gameState,
		className = "",
	}) => {
		const homeLead = homeTeam.score > awayTeam.score;
		const awayLead = awayTeam.score > homeTeam.score;

		return (
			<div
				className={`flex gap-3 items-center relative w-full max-w-[834px] mx-auto h-[140px] ${className}`}
			>
				{/* Team A (Home) */}
				<TeamScoreContainer
					team={homeTeam}
					gameState={gameState}
					align="left"
					hasAccentBar={homeLead}
				/>

				{/* Timer Container */}
				<div className="w-[130px] h-[140px] flex flex-col items-center justify-center shrink-0 border border-white/20 bg-kiosk-surface-secondary">
					{isProcessing ? (
						<div className="flex flex-col items-center justify-center gap-2 text-kiosk-accent">
							<Icon iconName="spinner_outline" size="XXXXLarge" loading />
							<p className="text-body-sm text-white">
								{t("kiosk.endgame.analyzing")}
							</p>
						</div>
					) : (
						<span className="text-display-h4-b text-white">{time}</span>
					)}
				</div>

				{/* Team B (Away) */}
				<TeamScoreContainer
					team={awayTeam}
					gameState={gameState}
					align="right"
					hasAccentBar={awayLead}
				/>
			</div>
		);
	},
);
